/**
 * OpenStreetMap Overpass API client.
 *
 * Finds real pharmacies, hospitals and clinics near the user. Free, no API
 * key required, and works well in Kenya (especially Nairobi, Mombasa, Kisumu).
 */

import { haversineKm, type Coords, type Facility, type FacilityType } from './care';

const ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

const AMENITY_TYPES: Record<string, FacilityType> = {
  pharmacy: 'pharmacy',
  hospital: 'hospital',
  clinic: 'clinic',
  doctors: 'clinic',
};

const HEALTHCARE_TYPES: Record<string, FacilityType> = {
  pharmacy: 'pharmacy',
  hospital: 'hospital',
  clinic: 'clinic',
  doctor: 'clinic',
};

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
}

interface OverpassResponse {
  elements: OverpassElement[];
}

function buildQuery(center: Coords, radiusMeters: number): string {
  const around = `around:${radiusMeters},${center.lat},${center.lon}`;
  return `[out:json][timeout:25];(
  node["amenity"~"^(pharmacy|hospital|clinic|doctors)$"](${around});
  way["amenity"~"^(pharmacy|hospital|clinic|doctors)$"](${around});
  node["healthcare"~"^(pharmacy|hospital|clinic|doctor)$"](${around});
  way["healthcare"~"^(pharmacy|hospital|clinic|doctor)$"](${around});
);out center 40;`;
}

/** Strip everything but digits and a leading + so tel: links work. */
function normalizePhone(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const first = raw.split(/[;,]/)[0]; // OSM sometimes lists several numbers
  const cleaned = first.replace(/[^\d+]/g, '');
  const digits = cleaned.replace(/\D/g, '');
  return digits.length >= 7 ? cleaned : undefined;
}

function defaultName(type: FacilityType): string {
  switch (type) {
    case 'pharmacy':
      return 'Pharmacy';
    case 'hospital':
      return 'Hospital';
    case 'clinic':
      return 'Clinic';
  }
}

function toFacility(element: OverpassElement, center: Coords): Facility | null {
  const tags = element.tags ?? {};
  const type =
    AMENITY_TYPES[tags.amenity ?? ''] ?? HEALTHCARE_TYPES[tags.healthcare ?? ''] ?? null;
  if (!type) return null;

  const lat = element.lat ?? element.center?.lat;
  const lon = element.lon ?? element.center?.lon;
  if (lat == null || lon == null) return null;

  const openingHours = tags.opening_hours;
  const address = [tags['addr:housenumber'], tags['addr:street']]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    id: `${element.type}-${element.id}`,
    name: tags.name ?? tags.brand ?? defaultName(type),
    type,
    lat,
    lon,
    distanceKm: haversineKm(center, { lat, lon }),
    phone: normalizePhone(
      tags.phone ??
        tags['contact:phone'] ??
        tags['contact:mobile'] ??
        tags.mobile ??
        tags['contact:whatsapp'],
    ),
    address: address || undefined,
    openingHours,
    open24h: /24\s*\/\s*7/.test(openingHours ?? ''),
    hasEmergency: tags.emergency === 'yes',
  };
}

async function fetchOverpass(url: string): Promise<OverpassResponse> {
  const res = await fetch(url, { signal: AbortSignal.timeout(30_000) });
  if (!res.ok) throw new Error(`Overpass responded ${res.status}`);
  return (await res.json()) as OverpassResponse;
}

/**
 * Query real facilities near `center`. Tries each public Overpass endpoint,
 * then falls back to the CORS proxy.
 */
export async function fetchNearbyFacilities(
  center: Coords,
  radiusMeters: number,
): Promise<Facility[]> {
  const query = buildQuery(center, radiusMeters);
  const encoded = encodeURIComponent(query);

  let lastError: unknown = null;
  const urls = [
    ...ENDPOINTS.map((endpoint) => `${endpoint}?data=${encoded}`),
    `${CORS_PROXY}${encodeURIComponent(`${ENDPOINTS[0]}?data=${encoded}`)}`,
  ];

  for (const url of urls) {
    try {
      const data = await fetchOverpass(url);
      const byName = new Map<string, Facility>();

      for (const element of data.elements) {
        const facility = toFacility(element, center);
        if (!facility) continue;

        // Nodes and building ways often describe the same place — keep the nearest.
        const key = facility.name.toLowerCase();
        const existing = byName.get(key);
        if (!existing || facility.distanceKm < existing.distanceKm) {
          byName.set(key, facility);
        }
      }

      return [...byName.values()]
        .sort((a, b) => a.distanceKm - b.distanceKm)
        .slice(0, 25);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to reach OpenStreetMap');
}
